import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { useKyrgyzstanOrderDetail } from 'entities/kyrgyzstan/lib'
import {
  kyrgyzstanDetailDataStore,
  kyrgyzstanOrdersStore,
} from 'entities/kyrgyzstan/model'

import type { CancellationPenaltyData } from 'shared/api/kyrgyzstan/orders'
import { KyrgyzstanOrdersApi } from 'shared/api/kyrgyzstan/orders'
import { getApiErrorMessage } from 'shared/lib/errorText'

import { getBookingDetails, getPaymentDetails } from './cancel-booking-utils'
import { useCancelWarningConfig } from './use-cancel-warning-config'

export const useCancelBookingPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { orderId } = useParams<{ orderId: string }>()

  const [isLoadingDecline, setIsLoadingDecline] = useState(false)
  const [errorDecline, setErrorDecline] = useState<string | null>(null)
  const [cancellationPenalty, setCancellationPenalty] =
    useState<CancellationPenaltyData | null>(null)
  const [isLoadingCancellationPenalty, setIsLoadingCancellationPenalty] =
    useState(false)
  const [errorCancellationPenalty, setErrorCancellationPenalty] = useState<
    string | null
  >(null)

  const {
    data: order,
    isLoading: isLoadingOrder,
    error: orderError,
  } = useKyrgyzstanOrderDetail(orderId)
  const realtyId = order?.realty_object.parent_id || order?.realty_object.id

  const {
    parentDetail,
    fetchParentDetail,
    resetParent,
    isParentLoading,
    parentError,
  } = kyrgyzstanDetailDataStore

  const realtyData = parentDetail?.id === realtyId ? parentDetail : null

  useEffect(() => {
    if (!realtyId) {
      resetParent()

      return
    }

    fetchParentDetail(realtyId)
  }, [realtyId, fetchParentDetail, resetParent])

  useEffect(
    () => () => {
      setErrorDecline(null)
    },
    [],
  )

  useEffect(() => {
    if (!orderId) {
      setCancellationPenalty(null)
      setErrorCancellationPenalty(null)
      return undefined
    }

    const abortController = new AbortController()

    const fetchCancellationPenalty = async () => {
      setIsLoadingCancellationPenalty(true)
      setErrorCancellationPenalty(null)
      setCancellationPenalty(null)

      try {
        const { data } = await KyrgyzstanOrdersApi.getCancellationPenalty(
          orderId,
          abortController.signal,
        )

        if (!abortController.signal.aborted) {
          setCancellationPenalty(data.data)
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          setErrorCancellationPenalty(
            getApiErrorMessage(error) ?? t('kyrgyzstan_block.error.title'),
          )
          setCancellationPenalty(null)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingCancellationPenalty(false)
        }
      }
    }

    fetchCancellationPenalty()

    return () => {
      abortController.abort()
    }
  }, [orderId, t])

  const handleCancel = async () => {
    if (!orderId) return

    setIsLoadingDecline(true)
    setErrorDecline(null)
    try {
      await KyrgyzstanOrdersApi.declineOrder(orderId)
      kyrgyzstanOrdersStore.clearLastActiveOrder()
      navigate(
        RoutingPaths.kyrgyzstan.myOrders.cancelBookingSuccess.path(orderId),
      )
    } catch (error) {
      setErrorDecline(getApiErrorMessage(error) ?? null)
    } finally {
      setIsLoadingDecline(false)
    }
  }

  const warningConfig = useCancelWarningConfig(order)

  const bookingDetails = useMemo(
    () => getBookingDetails(order, realtyData, t),
    [order, realtyData, t],
  )

  const paymentDetails = useMemo(
    () => getPaymentDetails(order, cancellationPenalty, t),
    [order, cancellationPenalty, t],
  )

  const isLoading =
    isLoadingOrder ||
    isParentLoading ||
    isLoadingCancellationPenalty ||
    (!order && !orderError) ||
    (!realtyData && !parentError) ||
    (!cancellationPenalty && !errorCancellationPenalty)

  return {
    order,
    realtyData,
    realtyError: orderError || parentError,
    cancellationPenalty,
    isLoading,
    errorCancellationPenalty,
    warningConfig,
    bookingDetails,
    paymentDetails,
    handleCancel,
    isCheckinWithin24h: order?.is_checkin_within_24h ?? false,
    paymentStatus: order?.payment_status ?? 'full',
    errorDecline,
    isLoadingDecline,
  }
}
